/**
 * Output collection → source raw-table mapping.
 *
 * Mirrors `supabase/functions/_shared/build-json.ts#buildAllCollections`.
 * The DiffPanel uses this to surface which output JSON files (uploaded to R2
 * under `<env>/<key>.<version>.json`) are *expected to change* given the
 * detected raw-table diff.
 *
 * Hardcoded on purpose: the mapping is small and rarely changes; spending
 * runtime cycles to introspect `build-json.ts` would not pay off.
 */
import { masterDataChannel } from '@rb/shared/master-data/cdn-config';
import { getSheetsBase } from '@/lib/cdnBase';

export const COLLECTION_SOURCES: Record<string, string[]> = {
  clientGameConfig: ['game_config', 'game_difficulty'],
  serverGameConfig: ['game_config', 'game_difficulty'],
  clientStages: [
    'stage',
    'stage_wave',
    'stage_wave_enemy',
    'enemy',
    'item',
    'meta_tag',
    'meta_tag_synergy',
    'world',
    'wave_shop_config',
    'item_group',
    'pet',
    'pet_grade',
    'pet_upgrade',
    'map',
  ],
  clientLeagueTables: [
    'league_table',
    'league_table_wave',
    'league_table_wave_enemy',
    'enemy',
    'item',
    'meta_tag',
    'meta_tag_synergy',
    'wave_shop_config',
    'item_group',
    'pet',
    'pet_grade',
    'pet_upgrade',
    'map',
  ],
  clientLeagueSeasons: ['league_season'],
  clientValhallaSeasons: ['valhalla_season'],
  clientDegenSeasons: ['degen_season'],
  clientDegenSeasonRewards: ['degen_season_reward'],
  clientValhallas: [
    'valhalla',
    'valhalla_wave',
    'valhalla_wave_enemy',
    'enemy',
    'item',
    'meta_tag',
    'meta_tag_synergy',
    'wave_shop_config',
    'item_group',
    'pet',
    'pet_grade',
    'pet_upgrade',
    'map',
  ],
  clientDegenLeagues: [
    'degen_league',
    'degen_league_wave',
    'degen_league_wave_enemy',
    'enemy',
    'item',
    'meta_tag',
    'meta_tag_synergy',
    'wave_shop_config',
    'item_group',
    'pet',
    'pet_grade',
    'pet_upgrade',
    'map',
  ],
  clientMaps: ['map'],
  clientCharacters: ['character', 'skin', 'upgrade_config', 'skin_unlock', 'skin_collection'],
  clientPets: ['pet', 'pet_grade', 'pet_upgrade', 'meta_tag', 'meta_tag_synergy'],
  clientPetSummonConfigs: ['pet_summon_config'],
  clientPetSummonLevels: ['pet_summon_level', 'pet_summon_probability'],
  clientSkills: ['skill'],
  clientConsumables: ['consumable_item'],
  clientQuests: ['quest'],
  clientQuestMilestones: ['quest_milestone'],
  clientGuideQuests: ['guide_quest'],
  clientAttendanceRewards: ['attendance_reward'],
  clientEventAttendanceRewards: ['event_attendance'],
  // 전환기 alias(web2 내용) — 실제 산출물이 시즌/디젠 참조 그래프로 필터링되므로
  // clientLeagueRewardsWeb2 와 동일한 소스 목록이어야 한다.
  clientLeagueRewards: ['league_reward', 'league_season', 'degen_league'],
  // flavor 별 산출물은 참조 그래프(시즌·디젠)로 필터링되므로 그 두 테이블도 소스다.
  clientLeagueRewardsWeb2: ['league_reward', 'league_season', 'degen_league'],
  clientLeagueRewardsWeb3: ['league_reward', 'league_season', 'degen_league'],
  clientLeagueSeasonsWeb2: ['league_season'],
  clientLeagueSeasonsWeb3: ['league_season'],
  clientProducts: ['product'],
  clientEntitlements: ['entitlement'],
  clientGloryShopItems: ['glory_shop_item'],
  clientGloryGachaEntries: ['glory_gacha_entry'],
  clientFreeShopItems: ['free_shop_item'],
  // server-only
  serverStages: ['stage'],
  serverLeagueSeasons: ['league_season'],
  serverLeagueSeasonsWeb2: ['league_season'],
  serverLeagueSeasonsWeb3: ['league_season'],
  serverLeagueRewardsWeb2: ['league_reward', 'league_season', 'degen_league'],
  serverLeagueRewardsWeb3: ['league_reward', 'league_season', 'degen_league'],
  serverValhallaSeasons: ['valhalla_season'],
  serverValhallas: ['valhalla', 'valhalla_wave'],
  serverCharacters: ['character', 'skin', 'upgrade_config', 'skin_unlock', 'skin_collection'],
  serverPets: ['pet', 'pet_grade', 'pet_upgrade'],
  serverPetSummonConfigs: ['pet_summon_config'],
  serverPetSummonLevels: ['pet_summon_level', 'pet_summon_probability'],
  serverGuideQuests: ['guide_quest'],
  serverAttendanceRewards: ['attendance_reward'],
  serverEventAttendanceRewards: ['event_attendance'],
  // 전환기 alias(web2 내용) — serverLeagueRewardsWeb2 와 동일한 소스 목록이어야 한다.
  serverLeagueRewards: ['league_reward', 'league_season', 'degen_league'],
  serverEntitlements: ['entitlement'],
  serverDegenRunePool: ['degen_rune_pool'],
  serverDegenConditionalPool: ['degen_conditional_pool'],
  serverDegenConditionalCountProb: ['degen_conditional_count_prob'],
  serverDegenSeasons: ['degen_season'],
  serverServerQuests: ['quest'],
  serverServerQuestMilestones: ['quest_milestone'],
  serverGloryShopItems: ['glory_shop_item'],
  serverGloryGachaEntries: ['glory_gacha_entry'],
  serverFreeShopItems: ['free_shop_item'],
};

/**
 * UTC `YYYYMMDDHHmm` — same scheme as `supabase/functions/apply/index.ts#formatVersion`.
 * Used for the "expected" R2 URL preview in DiffPanel; not load-bearing on
 * actual apply (the EF picks its own version at apply time).
 */
export function formatVersion(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}`;
}

export function buildR2Url(env: string, key: string, version: string): string {
  // EnvSchema 'production' → 'prod' R2 채널(apply EF 발행 위치). dev/staging identity.
  return `${getSheetsBase()}/${masterDataChannel(env)}/${key}.${version}.json`;
}
